import { useCallback, useEffect, useRef, useState } from 'react';
import ActionCable from 'actioncable';

const axios = require('axios').default;

const REFETCH_DEBOUNCE_MS = 1000;

const IDLE_TIMEOUT_MS = 5 * 60 * 1000;

export const useEntrySlots = ({ entrySlotsUrl, realTimeEnabled }) => {
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const consumerRef = useRef(null);
  const subscriptionsRef = useRef([]);
  const refetchTimer = useRef(null);
  const idleTimer = useRef(null);

  const latestDate = useRef(null);

  const fetchSlots = useCallback((forDate) => {
    if (!forDate) return Promise.resolve([]);

    latestDate.current = forDate;
    setLoading(true);
    setFailed(false);

    return axios
      .get(entrySlotsUrl, { params: { date: forDate } })
      .then(({ data }) => {
        if (latestDate.current !== forDate) return [];

        setSlots(data);
        setLoading(false);
        return data;
      })
      .catch(() => {
        if (latestDate.current !== forDate) return [];

        setFailed(true);
        setLoading(false);
        return [];
      });
  }, [entrySlotsUrl]);

  const teardown = useCallback(() => {
    subscriptionsRef.current.forEach((subscription) => subscription.unsubscribe());
    subscriptionsRef.current = [];
    clearTimeout(refetchTimer.current);
    clearTimeout(idleTimer.current);
  }, []);

  const subscribe = useCallback((forSlots, forDate) => {
    teardown();
    if (!realTimeEnabled || forSlots.length === 0) return;

    if (!consumerRef.current) {
      consumerRef.current = ActionCable.createConsumer('/cable');
    }

    const confirmIds = [...new Set(forSlots.map((slot) => slot.confirm_id))];

    subscriptionsRef.current = confirmIds.map((confirmId) => {
      let greeted = false;

      return consumerRef.current.subscriptions.create(
        { channel: 'ConfirmChannel', id: confirmId },
        {
          received: () => {
            if (!greeted) {
              greeted = true;
              return;
            }

            clearTimeout(refetchTimer.current);
            refetchTimer.current = setTimeout(() => fetchSlots(forDate), REFETCH_DEBOUNCE_MS);
          }
        }
      );
    });

    idleTimer.current = setTimeout(teardown, IDLE_TIMEOUT_MS);
  }, [fetchSlots, realTimeEnabled, teardown]);

  const selectDate = useCallback((nextDate) => {
    setDate(nextDate);
    setSlots([]);

    if (!nextDate) {
      latestDate.current = null;
      setLoading(false);
      teardown();
      return Promise.resolve([]);
    }

    return fetchSlots(nextDate).then((data) => {
      if (latestDate.current === nextDate) subscribe(data, nextDate);
      return data;
    });
  }, [fetchSlots, subscribe, teardown]);

  useEffect(() => teardown, [teardown]);

  return { date, slots, loading, failed, selectDate, retry: () => selectDate(date) };
};
